'use client';

import { useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import useCustomRouter from '@/hooks/useCustomRouter';
import usePath from '@/hooks/usePath';
import { ViewMode } from '@/components/molecules/view-toggle/types';
import { STANDALONE_FILTER_PARAMS } from '@/lib/algolia/filterable-attributes';

// EWM-185 / EWM-190 v2 PLP URL refinement.
//
// Translates filter / sort / pagination / view selections into the URL
// search-param shape the Frontastic product-list data source already
// understands:
//
//   facets[<facetId>][terms][N] = <termId>      multi-select term facet
//   facets[<facetId>][boolean]  = T              boolean facet
//   sortAttributes[0][<key>]    = asc | desc     sort
//   page                        = N              pagination (1-based, EWM-380)
//   view                        = list           grid (default) | list
//   limit / cursor                               legacy pagination (pre-EWM-380)
//
// Pushes via useCustomRouter so the route resolves the new params
// and the data source re-runs server-side. The view model reads
// the resulting facets[].selected back from the data source — no
// extra client state needed.
//
// EWM-379 / EWM-380: pagination is page-based (`?page=N`, fixed page size).
// The old "grow `limit`" Load More scheme tripped commercetools' 100-item
// Product Search cap on the 4th click → 404; a fixed page size never does.
//
// Filter / sort / clear-filter mutators all reset pagination so the user
// lands back on page 1 of the new filtered set (otherwise they could be on
// page 5 of a filter that only has 2 pages). View toggle keeps the page
// since it's purely cosmetic.
export type SortValue = 'recommended' | 'price-asc' | 'price-desc' | 'name-asc' | 'name-desc' | 'newest';

// EWM-386: 20 per page (was 24) — keeps grid rows even (the 5-/4-/2-col
// layouts all divide 20 cleanly) and avoids a ragged trailing row.
export const DEFAULT_PAGE_SIZE = 20;

const SORT_PARAM_PREFIX = 'sortAttributes';
const FACET_PARAM_PREFIX = 'facets[';

const VIEW_PARAM = 'view';
const PAGE_PARAM = 'page';
// Legacy pagination params (pre-EWM-380). Still read for backward-compat with
// old bookmarks, and cleared whenever we write the new `page` param.
const CURSOR_PARAM = 'cursor';
const LIMIT_PARAM = 'limit';

// Parse a commercetools cursor of the form "offset:N" -> N (0 if missing).
const offsetFromCursor = (cursor: string | null): number => {
  if (!cursor) return 0;
  const match = cursor.match(/offset:(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};

// Reset pagination back to page 1 — caller mutates `next` in place. Drops the
// page param plus the legacy cursor/limit so a filter/sort change always lands
// on the first page of the new result set.
const resetPagination = (next: URLSearchParams) => {
  next.delete(PAGE_PARAM);
  next.delete(CURSOR_PARAM);
  next.delete(LIMIT_PARAM);
};

const useFilterRefinement = () => {
  const router = useCustomRouter();
  const { pathWithoutQuery } = usePath();
  const params = useSearchParams();

  const push = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(params.toString());
      mutate(next);
      router.push(`${pathWithoutQuery}?${next.toString()}`);
    },
    [params, router, pathWithoutQuery],
  );

  const setTermFacet = useCallback(
    (facetId: string, values: string[]) => {
      push((next) => {
        // Remove existing term entries for this facet.
        Array.from(next.keys())
          .filter((k) => k.startsWith(`${FACET_PARAM_PREFIX}${facetId}][terms]`))
          .forEach((k) => next.delete(k));

        values.forEach((v, i) => next.set(`${FACET_PARAM_PREFIX}${facetId}][terms][${i}]`, v));
        resetPagination(next);
      });
    },
    [push],
  );

  const setBooleanFacet = useCallback(
    (facetId: string, value: boolean) => {
      push((next) => {
        next.delete(`${FACET_PARAM_PREFIX}${facetId}][boolean]`);
        if (value) next.set(`${FACET_PARAM_PREFIX}${facetId}][boolean]`, 'T');
        resetPagination(next);
      });
    },
    [push],
  );

  // EWM-240: set / clear a plain non-facet URL param (e.g.
  // `?showDiscontinued=T` — read directly by the product-list tastic,
  // not by the facet machinery). Setting to `null` removes the param.
  const setUrlFlag = useCallback(
    (param: string, value: string | null) => {
      push((next) => {
        next.delete(param);
        if (value !== null) next.set(param, value);
        resetPagination(next);
      });
    },
    [push],
  );

  const clearAllFacets = useCallback(() => {
    push((next) => {
      Array.from(next.keys())
        .filter((k) => k.startsWith(FACET_PARAM_PREFIX))
        .forEach((k) => next.delete(k));
      // Not just the `facets[` keys: every standalone filter param counts
      // toward the badge, so every one of them must be clearable (review B1).
      STANDALONE_FILTER_PARAMS.forEach((param) => next.delete(param));
      resetPagination(next);
    });
  }, [push]);

  // EWM-374: commit a batch of staged drawer selections in a SINGLE push so
  // the "Apply Filters" button triggers exactly one SSR re-run regardless of
  // how many sections changed. `staged` maps a facet id to its desired value:
  // a string[] for multi-select term facets (Brand / Category / dynamic
  // per-category sections) or a boolean for toggle facets (e.g. In Stock).
  // Mirrors setTermFacet / setBooleanFacet's param shapes exactly — it just
  // applies them all under one URLSearchParams mutation.
  const applyFacets = useCallback(
    (staged: Record<string, string[] | boolean>) => {
      push((next) => {
        Object.entries(staged).forEach(([facetId, value]) => {
          if (typeof value === 'boolean') {
            next.delete(`${FACET_PARAM_PREFIX}${facetId}][boolean]`);
            if (value) next.set(`${FACET_PARAM_PREFIX}${facetId}][boolean]`, 'T');
            return;
          }
          // Term facet: drop existing entries for this facet, then re-add.
          Array.from(next.keys())
            .filter((k) => k.startsWith(`${FACET_PARAM_PREFIX}${facetId}][terms]`))
            .forEach((k) => next.delete(k));
          value.forEach((v, i) => next.set(`${FACET_PARAM_PREFIX}${facetId}][terms][${i}]`, v));
        });
        resetPagination(next);
      });
    },
    [push],
  );

  const setSort = useCallback(
    (value: SortValue) => {
      push((next) => {
        Array.from(next.keys())
          .filter((k) => k.startsWith(SORT_PARAM_PREFIX))
          .forEach((k) => next.delete(k));

        if (value === 'price-asc') next.set(`${SORT_PARAM_PREFIX}[0][price]`, 'asc');
        else if (value === 'price-desc') next.set(`${SORT_PARAM_PREFIX}[0][price]`, 'desc');
        else if (value === 'name-asc') next.set(`${SORT_PARAM_PREFIX}[0][name]`, 'asc');
        else if (value === 'name-desc') next.set(`${SORT_PARAM_PREFIX}[0][name]`, 'desc');
        // EWM-255 — Newest only has one direction (desc) on the
        // `createdAt` Algolia attribute. Server-side `parseSort`
        // decodes the symmetric form.
        else if (value === 'newest') next.set(`${SORT_PARAM_PREFIX}[0][createdAt]`, 'desc');
        // 'recommended' = no sortAttributes param
        resetPagination(next);
      });
    },
    [push],
  );

  const setView = useCallback(
    (value: ViewMode) => {
      push((next) => {
        // Default 'grid' renders no param; 'list' is explicit so the
        // URL is shareable. View change does NOT reset pagination —
        // it's a purely cosmetic change.
        if (value === 'grid') next.delete(VIEW_PARAM);
        else next.set(VIEW_PARAM, value);
      });
    },
    [push],
  );

  const setPage = useCallback(
    (page: number) => {
      push((next) => {
        // EWM-380: page-based pagination — `?page=N` (1-based). Page 1 is a
        // clean URL (no param). EWM-379: always clear the legacy cursor/limit
        // so we never re-emit the old growing-`limit` scheme that tripped
        // commercetools' 100-item Product Search cap → 404.
        next.delete(CURSOR_PARAM);
        next.delete(LIMIT_PARAM);
        if (page <= 1) next.delete(PAGE_PARAM);
        else next.set(PAGE_PARAM, String(page));
      });
    },
    [push],
  );

  // Derive the current sort value from URL params for the dropdown's
  // controlled state.
  const currentSort: SortValue = (() => {
    const sortKey = Array.from(params.keys())
      .find((k) => k.startsWith(SORT_PARAM_PREFIX))
      ?.match(/sortAttributes\[0\]\[(.+)\]/)?.[1];
    if (!sortKey) return 'recommended';
    const vector = params.get(`${SORT_PARAM_PREFIX}[0][${sortKey}]`) ?? 'desc';
    if (sortKey === 'price') return vector === 'asc' ? 'price-asc' : 'price-desc';
    if (sortKey === 'name') return vector === 'asc' ? 'name-asc' : 'name-desc';
    // EWM-255 — `createdAt` only ships in desc form (Newest first);
    // ascending isn't a UX surface today.
    if (sortKey === 'createdAt') return 'newest';
    return 'recommended';
  })();

  // Derive view mode from URL with grid as default.
  const currentView: ViewMode = params.get(VIEW_PARAM) === 'list' ? 'list' : 'grid';

  // Derive the current page from `?page` (1-based), falling back to the
  // legacy cursor=offset:N scheme for old bookmarked URLs.
  const pageSize = parseInt(params.get(LIMIT_PARAM) ?? String(DEFAULT_PAGE_SIZE), 10);
  const currentPage = (() => {
    const pageParam = params.get(PAGE_PARAM);
    if (pageParam !== null) {
      const n = parseInt(pageParam, 10);
      return Number.isFinite(n) && n > 1 ? n : 1;
    }
    return Math.floor(offsetFromCursor(params.get(CURSOR_PARAM)) / pageSize) + 1;
  })();

  return {
    setTermFacet,
    setBooleanFacet,
    setUrlFlag,
    clearAllFacets,
    applyFacets,
    setSort,
    currentSort,
    setView,
    currentView,
    setPage,
    currentPage,
    pageSize,
  };
};

export default useFilterRefinement;
